import { formatMeasureValue } from '@ordo/constants/measure-format'
import { convertMeasure, measureGroupKey } from '@ordo/constants/measures'

import { perServingQuantity } from '@/lib/chefs/recipe-quantities'
import type { Campus, Component, MealtimeBlock } from '@/lib/chefs/types'

export interface MergedIngredient {
  key: string
  name: string
  unit: string
  quantity: number
  /** Resolved brand label — distinct brands of one ingredient stay separate rows. */
  brand: string | null
  /** FOOD / SUPPLY / UNCATEGORIZED — set only by the real aggregation rows
   *  (the recipe-walk fallback has no catalog data); enables the printed
   *  list's USDA-type grouping. */
  category?: 'FOOD' | 'SUPPLY' | 'UNCATEGORIZED'
  /** USDA CNDB category name; null/absent rows land in "Other". */
  usdaCategory?: string | null
}

/**
 * Merge key: one row per (ingredient, brand, *measurement group*).
 *
 * The group — not the raw unit — is what decides whether two amounts belong on
 * the same line. Keying on the raw string made `85 g` and `5.45 oz` of the same
 * canned black beans print as two unrelated ingredients (2026-08-06: Classic
 * Catering's packet showed `20945 g` and `27.25 oz` where the chef needed one
 * line reading 47.9 lb). Weight and volume still split, because combining them
 * needs a per-ingredient density we do not have.
 */
export function ingredientKey(name: string, unit: string, brand: string | null): string {
  return `${name.toLowerCase()}|${measureGroupKey(unit)}|${brand?.toLowerCase() ?? ''}`
}

function addQuantity(
  merged: Map<string, MergedIngredient>,
  name: string,
  unit: string,
  quantity: number,
  brand: string | null = null,
): void {
  const key = ingredientKey(name, unit, brand)
  const existing = merged.get(key)
  if (existing) {
    // Same group by construction, so this converts exactly: ounces added to a
    // gram row become grams. The first contribution's unit labels the row.
    existing.quantity += convertMeasure(quantity, unit, existing.unit)
  } else {
    merged.set(key, { key, name, unit, quantity, brand })
  }
}

/** Serving count one deliverable needs of a component (condiments ship multiples). */
function componentServings(deliverableQty: number, component: Component): number {
  return deliverableQty * (component.isCondiment ? (component.condimentQty ?? 1) : 1)
}

/** Fold one component's demand (`servings` servings) into the merge. */
function addComponentDemand(merged: Map<string, MergedIngredient>, c: Component, servings: number): void {
  if (!c.recipe) {
    // Recipe-less components (milk cartons, whole fruit, purchased items)
    // are still stock the kitchen pulls — one each per serving.
    addQuantity(merged, c.name, 'each', servings)
    return
  }
  // Recipe amounts are authored FOR THE RECIPE'S PRIMARY SERVING. A component
  // shipped at a non-primary serving must scale by that serving's ratio, or
  // the kitchen is told to pull a full primary portion for every child.
  // Brooks & Landry's CACFP PK menus ship 1/4-cup vegetables against 1-Cup
  // primaries, so this list asked for 4x the food (51,220 g of frozen
  // broccoli for a week needing 12,805 g — chef report 2026-08-31).
  const servingMultiplier = c.servingMultiplier ?? 1
  for (const ing of c.recipe.ingredients) {
    if (ing.quantity === null || ing.unit === null) {
      continue // no resolvable amount — nothing meaningful to sum
    }
    const perServing = perServingQuantity(ing.quantity, ing.quantityBasis, c.recipe.baseYield)
    if (perServing === null) {
      // Unresolvable yield — adding the raw BATCH amount here would inflate
      // the week's purchase list by the serving count. Unknown stays unknown.
      continue
    }
    addQuantity(merged, ing.name, ing.unit, perServing * servingMultiplier * servings, ing.brand ?? null)
  }
}

export function mergeMealtimeIngredients(block: MealtimeBlock): MergedIngredient[] {
  const merged = new Map<string, MergedIngredient>()
  for (const d of block.deliverables) {
    for (const c of d.components) {
      addComponentDemand(merged, c, componentServings(d.qty, c))
    }
  }
  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name))
}

export function mergeAllIngredients(campuses: Campus[]): MergedIngredient[] {
  const merged = new Map<string, MergedIngredient>()
  for (const campus of campuses) {
    for (const block of campus.mealtimes) {
      for (const ing of mergeMealtimeIngredients(block)) {
        addQuantity(merged, ing.name, ing.unit, ing.quantity, ing.brand)
      }
    }
    // Weekly milk rides its own campus-level section (never a deliverable),
    // but the cartons are still stock the kitchen pulls for this delivery.
    for (const milk of campus.milk ?? []) {
      addComponentDemand(merged, milk.component, milk.qty)
    }
  }
  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/** @deprecated Prefer `formatMeasureText` — this renders the number only. */
export function formatIngredientQuantity(quantity: number): string {
  return formatMeasureValue(quantity)
}
